/**
 * Auth Service Layer (V2)
 * 
 * Centralized service for all authentication-related API operations.
 * Uses V2 API client, AuthStore, and Notification system.
 */

import { apiClient } from "../lib/api/client";
import { AuthStore } from "../store/authStore";
import { notify } from "../lib/notifications";
import type {
    GoogleLoginResponse,
    UpdateUserTypeResponse,
    Institution,
    RegisterResponse,
    InstitutionAuthResponse
} from "../models/v2/auth";

/**
 * Auth Service
 * 
 * All methods throw ApiException on errors
 */
export const authService = {
    /**
     * Login or register via Google
     * 
     * POST /api/auth/google
     */
    async googleLogin(
        email: string,
        id: string,
        imageUrl: string,
        name?: string
    ): Promise<GoogleLoginResponse> {
        try {
            const response = await apiClient.post<GoogleLoginResponse>("/api/auth/google", {
                email,
                id,
                image_url: imageUrl,
                name,
            });

            // Persist session
            if (response.session && response.user) {
                AuthStore.setSession(
                    response.session.session_id,
                    response.session.expires_at,
                    response.user
                );
                notify.success(`Welcome back, ${response.user.name.split(' ')[0]}!`);
            } else {
                console.error("Invalid login response structure", response);
                throw new Error("Invalid login response from server");
            }

            return response;
        } catch (error) {
            // Error handling is largely done by apiClient (notifications), 
            // but we might want to catch specifically for login page UI state
            throw error;
        }
    },

    /**
     * Logout
     * 
     * POST /api/auth/logout
     */
    async logout(): Promise<void> {
        try {
            await apiClient.post("/api/auth/logout");
        } catch (error) {
            console.warn("Logout failed on backend, clearing local session anyway", error);
        } finally {
            AuthStore.clearSession();
            window.location.href = '/login';
        }
    },

    /**
     * Update user type/role
     * 
     * POST /api/auth/update-role
     */
    async updateUserType(
        type: string,
        google_id: string
    ): Promise<UpdateUserTypeResponse> {
        const response = await apiClient.post<UpdateUserTypeResponse>("/api/auth/update-role", {
            type,
            google_id,
        });

        // Update local user object if returned
        if (response.user) {
            const currentSession = AuthStore.getSessionId();
            // We can't easily get expiresAt if we didn't save it separately or if we don't have a specific getter exposed that returns it raw.
            // But we can just READ it from storage to preserve it.
            // A cleaner way is to just partial update user in store if we added a method for it.
            // For now, let's assume valid session implies valid expiry.
            const expiresAt = localStorage.getItem('auth_expires_at') || new Date().toISOString();
            AuthStore.setSession(currentSession!, expiresAt, response.user);
        }

        notify.success("Profile updated successfully");
        return response;
    },

    /**
     * Get all whitelisted institutions
     * 
     * GET /api/auth/institutions
     */
    async getInstitutions(): Promise<Institution[]> {
        return apiClient.get<Institution[]>("/api/auth/institutions");
    },

    /**
     * Register an institution user
     * 
     * POST /api/auth/institution/register
     */
    async registerInstitutionUser(payload: any): Promise<RegisterResponse> {
        return apiClient.post<RegisterResponse>("/api/auth/institution/register", payload);
    },

    /**
     * Verify institution OTP
     * 
     * POST /api/auth/institution/verify-otp
     */
    async verifyInstitutionOtp(payload: any): Promise<InstitutionAuthResponse> {
        const response = await apiClient.post<InstitutionAuthResponse>("/api/auth/institution/verify-otp", payload);

        // Persist session if successful
        if (response.session_id && response.user) {
            // Defaulting expires_at to 30 days if not returned by backend
            const expiresAt = response.expires_at || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
            AuthStore.setSession(
                response.session_id,
                expiresAt,
                response.user
            );
            notify.success(`Verification complete, welcome ${response.user.email}!`);
        }

        return response;
    },

    /**
     * Resend institution OTP
     * 
     * POST /api/auth/institution/resend-otp
     */
    async resendInstitutionOtp(payload: any): Promise<any> {
        const response = await apiClient.post("/api/auth/institution/resend-otp", payload);
        notify.success("New OTP sent to your email");
        return response;
    },

    /**
     * Login an institution user
     * 
     * POST /api/auth/institution/login
     */
    async loginInstitutionUser(payload: any): Promise<InstitutionAuthResponse> {
        const response = await apiClient.post<InstitutionAuthResponse>("/api/auth/institution/login", payload);

        // Persist session if successful
        if (response.session_id && response.user) {
            const expiresAt = response.expires_at || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
            AuthStore.setSession(
                response.session_id,
                expiresAt,
                response.user
            );
            notify.success(`Welcome back, ${response.user.email}!`);
        }

        return response;
    },
};
